import React, { useEffect } from 'react';
import styled from 'styled-components';
import { UnstyledLink } from '@zocdoc-frontend-common/components-link';
import { SignInForm } from '@zocdoc-frontend-common/components-sign-in-form';
import { blue100 } from '@zocdoc-mezzanine/foundations-colors';
import {
    headingLarge,
    labelSmall,
    paragraphSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import {
    withCustomMetrics,
    INTERACTION_TYPES,
    EVENT_INITIATORS,
    MetricsClickable,
} from '@zocdoc/frontend-metrics';
import { Metrics } from './constants';

/**
 *
 * @param {string} email
 * @param {function} sendMetric
 * @param {function} trackClickSignUp
 * @param {boolean} rememberMe
 * @param {boolean} showRememberMe
 * @returns {JSX.Element}
 * @constructor
 */
const BasePasswordFormView = ({
    email,
    sendMetric,
    trackClickSignUp,
    rememberMe,
    showRememberMe,
    ...props
}) => {
    useEffect(() => {
        sendMetric({
            action: Metrics.ViewPasswordForm,
            element: 'PasswordFormView',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
    }, [sendMetric]);

    const handleSignupLinkClick = () => {
        trackClickSignUp();

        sendMetric({
            action: Metrics.ClickCreateAccount,
            element: 'CreateAccountLink',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    return (
        <Wrapper>
            <Header>Enter your password</Header>
            <SignInForm
                {...props}
                isInMultiStepSignIn={true}
                defaultEmailValue={email}
                rememberMe={rememberMe}
                showRememberMe={showRememberMe}
                showFieldLabel={true}
            />
            <ActionText>
                {`New to Zocdoc? `}
                <ActionLink
                    beforeClick={handleSignupLinkClick}
                    href={
                        '/createuser/details?referrerType=EmailFirstSignInForm&component=PasswordFormView'
                    }
                    data-test={'sign-up-form-sign-up-link'}
                >
                    Create an account
                </ActionLink>
            </ActionText>
            <ActionText>
                <MetricsClickable
                    action={Metrics.ClickForgotPassword}
                    element={'ForgotPasswordLink'}
                >
                    <ActionLink
                        data-test="sign-in-forgot-password"
                        href="/auth/ForgotPassword?referrerType=EmailFirstSignInForm&component=PasswordFormView"
                    >
                        Forgot your password?
                    </ActionLink>
                </MetricsClickable>
            </ActionText>
        </Wrapper>
    );
};

export const PasswordFormView = withCustomMetrics({
    component: 'PasswordFormView',
})(BasePasswordFormView);

const Wrapper = styled.div`
    color: ${blue100};
`;

const Header = styled.h1`
    ${headingLarge};
    margin-bottom: 24px;
`;

const ActionText = styled.div`
    text-align: center;
    margin-top: 8px;
    ${paragraphSmall};
`;

const ActionLink = styled(UnstyledLink)`
    ${labelSmall};
    text-decoration: underline;
    cursor: pointer;
`;
